/** @typedef {'errors'|'performance'|'http'|string} Telemetry */
/** @typedef {import('aws-rum-web').AwsRum} AwsRum */

/**
 * @param {string|boolean|null|undefined} value
 * @param {boolean} [fallback]
 * @returns {boolean}
 */
function asBoolean(value, fallback = false) {
	if (value === undefined || value === null || value === '') {
		return fallback;
	}

	return value === 'true';
}

/**
 * @param {string|number|null|undefined} value
 * @param {number} fallback
 * @returns {number}
 */
function asNumber(value, fallback) {
	const parsed = Number(value);

	return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * @param {string|undefined} value
 * @returns {Telemetry[]}
 */
function parseTelemetries(value) {
	if (!value) {
		return ['errors', 'performance', 'http'];
	}

	try {
		const parsed = JSON.parse(value);

		if (Array.isArray(parsed)) {
			return parsed;
		}
	} catch {
		// Fall back to comma-separated parsing.
	}

	return value
		.split(',')
		.map((telemetry) => telemetry.trim())
		.filter(Boolean);
}

function getRegion() {
	return process.env.REACT_APP_CLOUDWATCH_RUM_APP_MONITOR_REGION;
}

function canUseBrowserRuntime() {
	return typeof window !== 'undefined';
}

function hasRequiredConfig() {
	return Boolean(
		process.env.REACT_APP_CLOUDWATCH_RUM_APP_MONITOR_ID && getRegion()
	);
}

function canAuthenticate() {
	if (process.env.REACT_APP_CLOUDWATCH_RUM_IDENTITY_POOL_ID) {
		return true;
	}

	return (
		asBoolean(process.env.REACT_APP_CLOUDWATCH_RUM_SIGNING, true) === false
	);
}

function canUse() {
	if (!canUseBrowserRuntime()) {
		return false;
	}

	if ((process.env.REACT_APP_ENV || 'local') === 'local') {
		return false;
	}

	if (!hasRequiredConfig()) {
		return false;
	}

	return canAuthenticate();
}

function getRumConfig() {
	return {
		allowCookies: asBoolean(
			process.env.REACT_APP_CLOUDWATCH_RUM_ALLOW_COOKIES,
			true
		),
		enableXRay: asBoolean(
			process.env.REACT_APP_CLOUDWATCH_RUM_ENABLE_XRAY,
			false
		),
		endpoint: process.env.REACT_APP_CLOUDWATCH_RUM_ENDPOINT || undefined,
		guestRoleArn:
			process.env.REACT_APP_CLOUDWATCH_RUM_GUEST_ROLE_ARN || undefined,
		identityPoolId:
			process.env.REACT_APP_CLOUDWATCH_RUM_IDENTITY_POOL_ID || undefined,
		sessionSampleRate: asNumber(
			process.env.REACT_APP_CLOUDWATCH_RUM_SESSION_SAMPLE_RATE,
			1
		),
		signing: asBoolean(process.env.REACT_APP_CLOUDWATCH_RUM_SIGNING, true),
		telemetries: parseTelemetries(
			process.env.REACT_APP_CLOUDWATCH_RUM_TELEMETRIES
		),
	};
}

/**
 * @returns {{ applicationId: string, region: string }|null}
 */
function getInitializationSettings() {
	const applicationId = process.env.REACT_APP_CLOUDWATCH_RUM_APP_MONITOR_ID;
	const region = getRegion();

	if (!applicationId || !region) {
		return null;
	}

	return {
		applicationId,
		region,
	};
}

/**
 * @param {unknown} error
 * @returns {Error}
 */
function normalizeError(error) {
	if (error instanceof Error) {
		return error;
	}

	if (typeof error === 'string') {
		return new Error(error);
	}

	return new Error(`Unknown CloudWatch RUM error: ${String(error)}`);
}

/** @type {AwsRum|null} */
let client = null;
/** @type {Promise<void>|null} */
let loadPromise = null;
/** @type {unknown[]} */
let pendingErrors = [];

function flushPendingErrors() {
	if (!client || pendingErrors.length === 0) {
		return;
	}

	const rumClient = client;

	pendingErrors.forEach((error) => {
		rumClient.recordError(normalizeError(error));
	});
	pendingErrors = [];
}

/**
 * @returns {Promise<AwsRum|null>}
 */
function loadClient() {
	if (client) {
		return Promise.resolve(client);
	}

	const settings = getInitializationSettings();

	if (!settings) {
		return Promise.resolve(null);
	}

	return import('aws-rum-web').then(
		({ AwsRum }) =>
			new AwsRum(
				settings.applicationId,
				process.env.REACT_APP_VERSION || '0.1.0',
				settings.region,
				getRumConfig()
			)
	);
}

const CloudWatchMonitor = {
	/**
	 * @param {unknown} error
	 * @returns {void}
	 */
	captureException(error) {
		CloudWatchMonitor.initialize();

		if (client) {
			client.recordError(normalizeError(error));
			return;
		}

		if (loadPromise) {
			pendingErrors.push(error);
		}
	},

	initialize() {
		if (client || loadPromise || !canUse()) {
			return;
		}

		loadPromise = loadClient()
			.then((loadedClient) => {
				if (!loadedClient || client) {
					return;
				}

				client = loadedClient;
				flushPendingErrors();
			})
			.catch((error) => {
				/* eslint-disable-next-line no-console -- Monitoring bootstrap failures should not crash the app */
				console.error('Failed to initialize CloudWatch RUM.', error);
				pendingErrors = [];
			})
			.finally(() => {
				loadPromise = null;
			});
	},
};

export default CloudWatchMonitor;
